import React, { useEffect } from 'react';
import { ArrowLeft, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SavedAVV, avvTemplateData, generateAVVTitle, weisungsprotokollColumns, replaceMainContractPlaceholder } from '@/lib/constants/avvTemplateData';
import { renderClauseContent } from './renderClauseContent';

interface AVVDocumentRendererProps {
  avv: SavedAVV;
  onBack: () => void;
}

const AVVDocumentRenderer: React.FC<AVVDocumentRendererProps> = ({ avv, onBack }) => {
  const { clauses, subProcessors, toms, annexes, processor } = avvTemplateData;

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-doc { 
            padding: 0 !important; 
            max-width: 100% !important;
            font-size: 11pt !important;
            line-height: 1.5 !important;
            color: #000 !important;
          }
          .print-doc h1 { font-size: 18pt !important; }
          .print-doc h2 { font-size: 14pt !important; page-break-after: avoid; }
          .print-doc h3, .print-doc h4 { font-size: 12pt !important; page-break-after: avoid; }
          .print-doc table { page-break-inside: avoid; }
          .print-doc .page-break { page-break-before: always; }
          .print-doc .signature-block { page-break-inside: avoid; }
          body { background: white !important; }
        }
      `}</style>

      {/* Toolbar */}
      <div className="no-print flex items-center justify-between mb-6">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zur Vorschau
        </Button>
        <Button onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Drucken / Als PDF speichern
        </Button>
      </div>

      {/* Document */}
      <div className="print-doc max-w-4xl mx-auto bg-background p-8 md:p-12 rounded-lg border space-y-6">
        {/* Title page */}
        <div className="text-center space-y-4 pb-8">
          <h1 className="text-2xl font-bold">Auftragsverarbeitungsvertrag</h1>
          <p className="text-lg">gemäß Art. 28 Abs. 3 DSGVO</p>
          <p className="text-muted-foreground">{generateAVVTitle(avv.controller.name)}</p>
        </div>

        <hr />

        {/* Parties */}
        <div className="grid grid-cols-2 gap-8 text-sm">
          <div>
            <p className="font-bold mb-1">Auftraggeber (Verantwortlicher):</p>
            <p>{avv.controller.name}</p>
            <p>{avv.controller.address}</p>
            {avv.controller.representedBy && <p>Vertreten durch: {avv.controller.representedBy}</p>}
            {avv.controller.dataProtectionOfficer && <p>DSB: {avv.controller.dataProtectionOfficer} ({avv.controller.dpoEmail})</p>}
          </div>
          <div>
            <p className="font-bold mb-1">Auftragnehmer (Auftragsverarbeiter):</p>
            <p>{processor.name}</p>
            <p>{processor.address}</p>
            <p>Vertreten durch: {processor.representedBy}</p>
            <p>DSB: {processor.dataProtectionOfficer} ({processor.dpoEmail})</p>
          </div>
        </div>

        {avv.project.contractStart && (
          <p className="text-sm"><strong>Laufzeit:</strong> {avv.project.contractStart} bis {avv.project.contractEnd || 'unbefristet'}</p>
        )}

        <hr />

        {/* Clauses */}
        {clauses.map(clause => {
          const content = replaceMainContractPlaceholder(clause.content, avv.project.mainContractReference);
          return (
            <div key={clause.id} className="space-y-2">
              <h2 className="text-base font-bold mt-4">{clause.title}</h2>
              <div className="text-sm">{renderClauseContent(content)}</div>
            </div>
          );
        })}

        {/* Annexes */}
        <div className="page-break" />
        <h2 className="text-base font-bold">{annexes[0].title}</h2>
        <p className="text-sm">{annexes[0].content}</p>
        {toms.map((tom, i) => (
          <div key={i}>
            <h4 className="font-semibold text-sm mt-2">{tom.title}</h4>
            <div className="text-sm space-y-1">
              {tom.measures.map((m, j) => (
                <div key={j} className="flex gap-2 pl-4">
                  <span className="shrink-0">•</span>
                  <span>{m}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="page-break" />
        <h2 className="text-base font-bold">{annexes[1].title}</h2>
        <table className="w-full text-xs border-collapse border mt-2">
          <thead>
            <tr>
              {['Unterauftragnehmer', 'Funktion', 'Standort', 'Rechtsgrundlage', 'Daten'].map(h => (
                <th key={h} className="border p-1 text-left font-semibold bg-muted/30">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {subProcessors.map((sp, i) => (
              <tr key={i}>
                <td className="border p-1">{sp.name}</td>
                <td className="border p-1">{sp.function}</td>
                <td className="border p-1">{sp.location}</td>
                <td className="border p-1">{sp.legalBasis}</td>
                <td className="border p-1">{sp.dataProcessed}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="page-break" />
        <h2 className="text-base font-bold">{annexes[2].title}</h2>
        <div className="text-sm whitespace-pre-line">{annexes[2].content}</div>

        <h2 className="text-base font-bold mt-4">{annexes[3].title}</h2>
        <div className="text-sm whitespace-pre-line">{annexes[3].content}</div>
        <table className="w-full text-xs border-collapse border mt-2">
          <thead>
            <tr>
              {weisungsprotokollColumns.map(col => (
                <th key={col} className="border p-1 text-left font-semibold bg-muted/30">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border p-1">1</td>
              {Array(6).fill(null).map((_, i) => (
                <td key={i} className="border p-1">&nbsp;</td>
              ))}
            </tr>
          </tbody>
        </table>

        {/* Signatures */}
        <div className="signature-block grid grid-cols-2 gap-12 pt-12 mt-8">
          <div>
            <p className="text-sm mb-8">Ort, Datum</p>
            <div className="border-b border-black w-full mb-2" />
            <p className="text-sm">{avv.controller.name}</p>
            <p className="text-xs">(Auftraggeber)</p>
          </div>
          <div>
            <p className="text-sm mb-8">Ort, Datum</p>
            <div className="border-b border-black w-full mb-2" />
            <p className="text-sm">{processor.name}</p>
            <p className="text-xs">(Auftragnehmer)</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AVVDocumentRenderer;
